import { useState } from "react";
import axios from "axios";
import { Check, Loader2 } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Reveal, Overline } from "@/components/primitives";
import { scrollToContact } from "@/lib/scroll";
import {
  plans,
  planPricing,
  planTrial,
  planCapabilities,
  locationLabel,
  planCta,
  recommendedPlanId,
} from "@/config/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function PlanCard({ plan }) {
  const [loading, setLoading] = useState(false);
  const pricing = planPricing(plan);
  const trial = planTrial(plan);
  const caps = planCapabilities(plan);
  const cta = planCta(plan);
  const recommended = plan.id === recommendedPlanId;
  const lookupKey = pricing.lookup_key;

  const onCta = async () => {
    if (!lookupKey) {
      scrollToContact();
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        lookup_key: lookupKey,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error("Couldn't start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div
      data-testid={`pricing-card-${plan.id}`}
      className={`relative flex h-full flex-col rounded-[10px] bg-white p-8 transition-transform duration-300 hover:-translate-y-1 ${
        recommended ? "border-2 border-black shadow-[0_30px_70px_-40px_rgba(0,0,0,0.5)]" : "border border-black/10"
      }`}
    >
      {recommended && (
        <div data-testid="pricing-recommended-badge" className="absolute -top-3 left-8 rounded-[8px] bg-black px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-white">
          Recommended
        </div>
      )}

      <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-black/45">
        {locationLabel(plan.max_locations)}
      </div>
      <h3 className="mt-2 text-xl font-semibold tracking-tight text-black">{plan.name}</h3>

      <div className="mt-5 flex items-end gap-1">
        <span className="text-4xl font-black tracking-tighter text-black">{pricing.label}</span>
        {pricing.sub && <span className="pb-1.5 text-sm font-light text-black/50">{pricing.sub}</span>}
      </div>

      {trial ? (
        <div className="mt-3 rounded-[8px] bg-black/[0.04] px-3 py-2 text-[11px] font-medium text-black/70">
          Free trial available · {trial.days} days
          {!trial.cardRequired && " · no credit card required"}
        </div>
      ) : (
        <div className="mt-3 h-[34px]" />
      )}

      <button
        data-testid={`pricing-cta-${plan.id}`}
        onClick={onCta}
        disabled={loading}
        className={`mt-6 inline-flex w-full items-center justify-center gap-2 rounded-[10px] py-3.5 text-sm font-semibold transition-colors duration-200 disabled:opacity-60 ${
          recommended
            ? "bg-black text-white hover:bg-black/90"
            : "border border-black/20 text-black hover:border-black hover:bg-black/[0.03]"
        }`}
      >
        {loading && <Loader2 size={15} className="animate-spin" />}
        {loading ? "Starting…" : cta}
      </button>

      <ul className="mt-8 space-y-3 border-t border-black/[0.08] pt-6">
        {caps.map((c) => (
          <li key={c} className="flex items-center gap-2.5 text-sm text-black/70">
            <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-black">
              <Check size={10} className="text-white" />
            </span>
            {c}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Pricing() {
  return (
    <section id="pricing" className="scroll-mt-20 bg-white py-24 lg:py-32">
      <div className="mx-auto max-w-[1280px] px-5 sm:px-8">
        <div className="max-w-3xl">
          <Reveal><Overline>Pricing</Overline></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-6 text-4xl font-black leading-[1.02] tracking-tighter text-black sm:text-5xl lg:text-6xl">
              Choose the right plan
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-xl text-lg font-light leading-relaxed text-black/60">
              Simple, self-serve plans built for local businesses. Start free and grow into automation as you go.
            </p>
          </Reveal>
        </div>

        <div className="mt-16 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {plans.map((plan, i) => (
            <Reveal key={plan.id} delay={(i % 3) * 0.06} className="h-full">
              <PlanCard plan={plan} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
